import apiService from './api'

export interface Booking {
  _id: string
  schoolId: string
  courseId?: string
  userId?: string
  status: 'pending' | 'approved' | 'cancelled' | 'rejected'
  substatus?: 'pending payment' | 'pending approval from school'
  name: string
  email: string
  phone?: string
  message?: string
  selectedMonth?: string
  accommodationType?: string
  paymentLink?: string
  actionTimeline?: Array<{
    action: string
    substatus?: 'pending payment' | 'pending approval from school'
    status: 'pending' | 'approved' | 'cancelled' | 'rejected'
    performedBy: 'USER' | 'SCHOOL' | 'SYSTEM'
    performedAt: string
    notes?: string
  }>
  notes?: string
  submittedAt: string
  approvedAt?: string
  rejectedAt?: string
  paymentCompletedAt?: string
  createdAt: string
  updatedAt: string
  schoolName?: string
  courseTitle?: string
}

export interface BookingStats {
  totalBookings: number
  approvedBookings: number
  rejectedBookings: number
  cancelledBookings: number
  pendingPaymentBookings: number
  pendingApprovalBookings: number
  recentBookings: number
}

class BookingsService {
  /**
   * Get all bookings for the school (POST request)
   */
  async getBookings(params?: {
    page?: number
    limit?: number
    sort?: string
    order?: 'asc' | 'desc'
    status?: string
    substatus?: string
    actionStatus?: string  // Keep for backward compatibility, maps to substatus
    courseId?: string
    search?: string
  }): Promise<{ success: boolean; data: { bookings: Booking[]; pagination: any } }> {
    try {
      // Map actionStatus to substatus if provided
      const requestParams = { ...params }
      if (requestParams.actionStatus && !requestParams.substatus) {
        // Map old actionStatus values to substatus
        if (requestParams.actionStatus === 'Y_PENDING') {
          requestParams.substatus = 'pending approval from school'
        } else if (requestParams.actionStatus === 'U_PENDING') {
          requestParams.substatus = 'pending payment'
        }
        delete requestParams.actionStatus
      }
      const response = await apiService.post('/bookings/search', requestParams || {})
      return response
    } catch (error) {
      console.error('Error fetching bookings:', error)
      throw error
    }
  }

  /**
   * Get booking by ID
   */
  async getBooking(bookingId: string): Promise<{ success: boolean; data: Booking }> {
    try {
      const response = await apiService.get(`/bookings/${bookingId}`)
      return response
    } catch (error) {
      console.error('Error fetching booking:', error)
      throw error
    }
  }

  /**
   * Approve booking
   */
  async approveBooking(
    bookingId: string, 
    notes?: string
  ): Promise<{ success: boolean; data: Booking }> {
    try {
      const response = await apiService.post(`/bookings/${bookingId}/approve`, {
        notes
      })
      return response
    } catch (error) {
      console.error('Error approving booking:', error)
      throw error
    }
  }

  /**
   * Reject booking
   */
  async rejectBooking(
    bookingId: string, 
    notes?: string
  ): Promise<{ success: boolean; data: Booking }> {
    try {
      const response = await apiService.post(`/bookings/${bookingId}/reject`, {
        notes
      })
      return response
    } catch (error) {
      console.error('Error rejecting booking:', error)
      throw error
    }
  }

  /**
   * Update booking
   */
  async updateBooking(
    bookingId: string, 
    data: Partial<Booking>
  ): Promise<{ success: boolean; data: Booking }> {
    try {
      const response = await apiService.put(`/bookings/${bookingId}`, data)
      return response
    } catch (error) {
      console.error('Error updating booking:', error)
      throw error
    }
  }

  /**
   * Get booking statistics
   */
  async getBookingStats(): Promise<{ success: boolean; data: { stats: BookingStats } }> {
    try {
      const response = await apiService.get('/bookings/stats')
      return response
    } catch (error) {
      console.error('Error fetching booking stats:', error)
      throw error
    }
  }
}

export default new BookingsService()

